// Iter 548 — Panel Antrean Shopee Sablon/DTF (Ibnu / Fatir).
//
// Dipakai hanya di dalam ProductionQueue untuk operator team="Sablon/DTF".
// Menyambungkan antrean DTF Shopee dari `GET /api/shopee/sablon/queue`
// dgn aksi `POST /api/shopee/{job_id}/sablon/start` & `/sablon/done`.
//
// Tidak mengubah alur Offline (Ibnu tetap melihat antrean offline di bawah
// komponen ini pada ProductionQueue). HOLD/BATAL otomatis dihormati oleh
// backend — kartu HOLD disembunyikan dari queue response.
import { ShopeeCancelTag } from "./shopee_cancel_tag";
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function _fmtDate(iso) {
  if (!iso || typeof iso !== "string") return "";
  const s = iso.length >= 10 ? iso.slice(0, 10) : iso;
  const p = s.split("-");
  if (p.length !== 3) return s;
  return `${p[2]}/${p[1]}/${p[0].slice(2)}`;
}

// Iter 551 — Ambil file_id mockup FIX terbaru untuk thumbnail.
//   Prioritas: snapshot yg dikirim ke Gudang (final FIX). Fallback: mockup
//   aktif terbaru dari array `mockups` (job belum dikirim ke Gudang / paralel).
//   Mengembalikan "" jika tidak ada file valid (UI tampilkan placeholder).
function pickMockupFileId(job) {
  const snap = (job?.warehouse_sent_snapshot_file_ids || [])[0];
  if (snap) return snap;
  const mockups = (job?.mockups || []).filter(m => !m?.deleted_at);
  if (!mockups.length) return "";
  // Mockups terurut created_at asc; ambil yang terbaru.
  const last = mockups[mockups.length - 1];
  const ver = (last?.versions || []).find(v => v?.version === last?.current_version);
  return ver?.file_id || (last?.versions || [])[0]?.file_id || "";
}

function JobCard({ job, onStart, onDone, busy }) {
  const [note, setNote] = useState("");
  const [zoomUrl, setZoomUrl] = useState("");
  const thumbId = pickMockupFileId(job);
  // Thumbnail ringan: max 300 px (JPEG Q72 di backend, cache 24 jam).
  const thumbUrl = thumbId
    ? `${API}/shopee/files/${thumbId}/thumbnail?size=300`
    : "";
  const fullUrl = thumbId
    ? `${API}/shopee/files/${thumbId}/preview`
    : "";
  const inProgress = job.sablon_state === "in_progress";
  const forwarded = job.sablon_state === "forwarded";
  // Iter 549 — HOLD aktif → semua tombol DISABLED. Label jelas + alasan.
  const hold = job.hold || {};
  const isHold = !!hold.active;
  const processLbl = job.process === "bordir_sablon_dtf" ? "Bordir + Sablon DTF" : "Sablon DTF";
  // Iter 551 — Field opsional (tampil hanya kalau ada; TIDAK diarang-arang).
  const totalPcs = Number(job.total_qty || job.total_pcs || 0) || null;
  const warna = job.color || job.warna || "";
  const ukuran = job.size || job.ukuran || "";
  const productName = job.product_name || job.item_name || "";
  const forwardAt = job.sablon_forwarded_at || job.first_fix_at;
  return (
    <div className="shopee-sablon-card" data-testid={`sablon-card-${job.id}`}>
      <ShopeeCancelTag job={job}/>
      {isHold && (
        <div className="sablon-hold-banner" data-testid={`sablon-hold-banner-${job.id}`}>
          <strong>⛔ PEKERJAAN DIHENTIKAN</strong>
          {hold.status && <span> · {hold.status === "pending" ? "menunggu keputusan" : "dihold Rido"}</span>}
          {hold.reason && <div className="sablon-hold-reason">Alasan: {hold.reason}</div>}
          {hold.requested_by_name && <div className="muted small">Diajukan oleh: {hold.requested_by_name}</div>}
        </div>
      )}
      <div className="sablon-card-row">
        {thumbUrl ? (
          <button
            type="button"
            className="sablon-thumb-btn"
            onClick={() => setZoomUrl(fullUrl)}
            aria-label="Lihat mockup ukuran besar"
            data-testid={`sablon-thumb-${job.id}`}
          >
            <img
              src={thumbUrl}
              alt="mockup"
              className="sablon-thumb-img"
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.parentElement.classList.add("err"); }}
            />
          </button>
        ) : (
          <div className="sablon-thumb-placeholder" data-testid={`sablon-thumb-missing-${job.id}`}>
            <span>Mockup belum tersedia</span>
          </div>
        )}
        <div className="sablon-card-info">
          <div className="sablon-card-order" title={job.order_number}>
            {job.order_number}
          </div>
          <div className="sablon-card-customer muted">
            {job.customer_account || "—"}
          </div>
          {productName && (
            <div className="sablon-card-product">{productName}</div>
          )}
          {(warna || ukuran || totalPcs) && (
            <div className="sablon-card-attrs muted">
              {warna && <span>Warna: <b>{warna}</b></span>}
              {ukuran && <span>Ukuran: <b>{ukuran}</b></span>}
              {totalPcs && <span>Qty: <b>{totalPcs} pcs</b></span>}
            </div>
          )}
          <div className="sablon-card-process">
            <span className="sablon-chip sablon-chip-proc">{processLbl}</span>
            {job.job_type && job.job_type !== "reguler" && (
              <span className="sablon-chip sablon-chip-type">{job.job_type}</span>
            )}
            {forwardAt && (
              <span className="muted small">Masuk antrean: {_fmtDate(forwardAt)}</span>
            )}
          </div>
          {job.design_title && (
            <div className="muted small sablon-card-title" title={job.design_title}>
              {job.design_title}
            </div>
          )}
        </div>
      </div>
      {(forwarded || inProgress) && !isHold && (
        <div className="sablon-card-actions">
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Catatan (opsional)"
            data-testid={`sablon-note-${job.id}`}
            className="input small sablon-card-note"
            disabled={busy}
          />
          {forwarded && (
            <button
              type="button"
              className="btn primary sablon-card-primary-btn"
              onClick={() => onStart(job.id, note)}
              disabled={busy}
              data-testid={`sablon-start-${job.id}`}
            >
              Mulai DTF
            </button>
          )}
          {inProgress && (
            <button
              type="button"
              className="btn primary sablon-card-primary-btn"
              onClick={() => onDone(job.id, note)}
              disabled={busy}
              data-testid={`sablon-done-${job.id}`}
            >
              Selesai DTF
            </button>
          )}
        </div>
      )}
      {zoomUrl && (
        <div
          className="sablon-zoom-overlay"
          onClick={() => setZoomUrl("")}
          role="button"
          aria-label="Tutup pratinjau"
          data-testid={`sablon-zoom-overlay-${job.id}`}
        >
          <img src={zoomUrl} alt="mockup besar" className="sablon-zoom-img" />
        </div>
      )}
    </div>
  );
}

export default function ShopeeSablonPanel({ operator }) {
  const [tab, setTab] = useState("antrean"); // antrean | dikerjakan | selesai | ditahan | riwayat
  const [data, setData] = useState({ antrean: [], dikerjakan: [], selesai: [], ditahan: [], riwayat: [], counts: {} });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const r = await axios.get(`${API}/shopee/sablon/queue`, {
        params: { include_history: tab === "riwayat" }, withCredentials: true, timeout: 15_000,
      });
      setData(r?.data || {});
    } catch (e) {
      if (e?.response?.status === 403) {
        setData({});  // sembunyi diam-diam
      } else {
        setErr(e?.response?.data?.detail || e?.message || "Gagal memuat antrean Sablon");
      }
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const act = async (url, jobId, note) => {
    setBusy(true);
    try {
      await axios.post(`${API}/shopee/${jobId}${url}`, { note: note || "" }, { withCredentials: true, timeout: 15_000 });
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Gagal memperbarui";
      alert(msg);
    } finally {
      setBusy(false);
    }
  };

  const list = (() => {
    if (tab === "antrean") return data.antrean || [];
    if (tab === "dikerjakan") return data.dikerjakan || [];
    if (tab === "selesai") return data.selesai || [];
    if (tab === "ditahan") return data.ditahan || [];
    return data.riwayat || [];
  })();

  const counts = data.counts || {};
  const tabs = [
    { key: "antrean", label: "Antrean", count: counts.antrean || 0 },
    { key: "dikerjakan", label: "Dikerjakan", count: counts.dikerjakan || 0 },
    { key: "selesai", label: "Selesai DTF", count: counts.selesai_dtf || 0 },
    { key: "ditahan", label: "Ditahan", count: counts.ditahan || 0 },
    { key: "riwayat", label: "Riwayat", count: counts.riwayat || 0 },
  ];

  return (
    <section className="shopee-sablon-panel" data-testid="shopee-sablon-panel">
      <div className="shopee-sablon-head">
        <h3 className="shopee-sablon-title" data-testid="shopee-sablon-title">
          Shopee — Sablon / DTF
        </h3>
        <small className="muted" data-testid="shopee-sablon-subtitle">
          PIC: {operator?.name || "-"} · Dua jalur paralel (Gudang + DTF)
        </small>
      </div>
      <div role="tablist" aria-label="Tab antrean sablon" className="shopee-sablon-tabs">
        {tabs.map(t => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            data-testid={`shopee-sablon-tab-${t.key}`}
            className={`sablon-tab-btn${tab === t.key ? " active" : ""}`}
          >
            {t.label} ({t.count})
          </button>
        ))}
        <button type="button" onClick={load} className="link-btn" style={{ marginLeft: "auto", fontSize: 11 }} data-testid="shopee-sablon-refresh">
          ⟳ Refresh
        </button>
      </div>
      {err && <div className="warn-box small" data-testid="shopee-sablon-err">{err}</div>}
      {loading && <div className="muted small">Memuat…</div>}
      {!loading && !err && list.length === 0 && (
        <div className="shopee-sablon-empty muted small" data-testid="shopee-sablon-empty">
          {tab === "antrean" ? "Belum ada pekerjaan DTF menunggu." :
           tab === "dikerjakan" ? "Belum ada DTF yang sedang dikerjakan." :
           tab === "selesai" ? "Belum ada cetakan DTF siap." :
           tab === "ditahan" ? "Tidak ada pekerjaan DTF yang sedang dihentikan." :
           "Belum ada riwayat DTF."}
        </div>
      )}
      {!loading && !err && list.length > 0 && (
        <div className="shopee-sablon-list" data-testid="shopee-sablon-list">
          {list.map(j => (
            <JobCard
              key={j.id}
              job={j}
              busy={busy}
              onStart={(id, n) => act("/sablon/start", id, n)}
              onDone={(id, n) => act("/sablon/done", id, n)}
            />
          ))}
        </div>
      )}
    </section>
  );
}
